import type { RoleEnum } from '@zeus/index'
import { useMutation } from '@apollo/client/react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { BrandLogo } from '~/components/BrandLogo'
import { MAIN_ID } from '~/components/SkipLink'
import Spinner from '~/components/Spinner'
import { Button } from '~/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/components/ui/card'
import { GQL_LOGIN_MAGIC_LINK } from '~/gql/auth/auth'
import { useAuth } from '~/helpers/store/auth'
import { getDefaultRoute } from '~/routes/protected-loader'

/**
 * Atterrissage du lien reçu par email (#39). Le jeton est consommé
 * automatiquement : il est à usage unique et déjà en main, un clic de
 * confirmation supplémentaire n'apporterait aucune garantie.
 */
function MagicLink() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const { setPayload, setLoading } = useAuth()

  const token = searchParams.get('token')
  const [failed, setFailed] = useState(false)
  // Les liens sont parfois préchargés par le client mail : StrictMode ou un
  // re-render ne doit pas déclencher une seconde consommation.
  const consumed = useRef(false)

  const [loginWithMagicLink] = useMutation(GQL_LOGIN_MAGIC_LINK)

  useEffect(() => {
    if (!token) {
      navigate('/', { replace: true })
      return
    }
    if (consumed.current) return
    consumed.current = true

    const run = async () => {
      try {
        const { data } = await loginWithMagicLink({ variables: { token } })
        if (data?.loginWithMagicLink.__typename === 'UserInfo') {
          const user = data.loginWithMagicLink
          setPayload({
            firstName: user.firstName,
            lastName: user.lastName,
            email: user.email,
            role: user.roles,
            id: user.id,
          })
          setLoading(false)
          toast.success(t('connection.successful'))
          // `replace` : l'URL porte le jeton en clair, elle ne doit pas rester
          // dans l'historique (bouton Précédent, restauration de session).
          navigate(getDefaultRoute(user.roles as RoleEnum), { replace: true })
        } else {
          setFailed(true)
        }
      } catch {
        setFailed(true)
        toast.error(t('bad.server-login.message'))
      }
    }

    void run()
  }, [token, navigate, loginWithMagicLink, setPayload, setLoading, t])

  return (
    <main id={MAIN_ID} className="flex min-h-dvh w-full flex-col items-center justify-center p-5">
      <Card className="sm:w-md w-full">
        <CardHeader className="gap-4">
          <BrandLogo className="h-9 w-auto" fallbackClassName="text-lg" />
          <div className="flex flex-col gap-1">
            <CardTitle as="h1" className="font-heading text-2xl tracking-tight">
              {failed ? t('magic-link.failed.title') : t('magic-link.checking')}
            </CardTitle>
            <CardDescription>
              {failed ? t('magic-link.failed.description') : t('magic-link.checking.description')}
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          {failed
            ? (
                <Button className="w-full" onClick={() => navigate('/login')}>
                  {t('magic-link.request-new')}
                </Button>
              )
            : (
                <div className="flex justify-center py-2">
                  <Spinner className="size-6 text-muted-foreground" />
                </div>
              )}

          <div className="mt-4 text-center text-sm">
            <Link to="/login" className="underline underline-offset-4">
              {t('login.return-message')}
            </Link>
          </div>
        </CardContent>
      </Card>
    </main>
  )
}

export default MagicLink
